import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Club } from '@court-sync/shared';
import { publicTenantsService, type PublicTenantSubdomainResponse } from '@/services/publicTenants';
import { getTenantSubdomainContext, type TenantSubdomainContext } from '@/lib/tenantHost';

interface ClubHostContextValue {
  host: TenantSubdomainContext;
  club: Club | null;
  /** Path slug from loaded club (for APIs); undefined until loaded. */
  pathSlug: string | undefined;
  loading: boolean;
  error: string | null;
  /** When tenant is academy subdomain with entitled club shell */
  siblingClub: Club | null;
  tenantType: PublicTenantSubdomainResponse['type'] | null;
}

const Ctx = createContext<ClubHostContextValue | null>(null);

export function ClubHostProvider({
  children,
  hostContext,
}: {
  children: React.ReactNode;
  hostContext?: TenantSubdomainContext;
}) {
  const host = hostContext ?? getTenantSubdomainContext();
  const [club, setClub] = useState<Club | null>(null);
  const [siblingClub, setSiblingClub] = useState<Club | null>(null);
  const [tenantType, setTenantType] = useState<PublicTenantSubdomainResponse['type'] | null>(null);
  const [loading, setLoading] = useState(host.mode === 'subdomain');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (host.mode !== 'subdomain' || !host.subdomainLabel) {
      return undefined;
    }

    let mounted = true;
    void Promise.resolve().then(() => {
      setLoading(true);
      setError(null);
    });

    publicTenantsService
      .getBySubdomain(host.subdomainLabel!)
      .then((res) => {
        if (!mounted) return;
        if (!res.success || !res.data) {
          setClub(null);
          setSiblingClub(null);
          setTenantType(null);
          setError(res.error || 'Club not found');
          setLoading(false);
          return;
        }
        setTenantType(res.data.type);
        const publicClub = res.data.club ?? null;
        const activeClub =
          publicClub && !publicClub.isInternalShell ? publicClub : null;
        setClub(activeClub);
        setSiblingClub(publicClub);
        setError(null);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (mounted) {
          setClub(null);
          setSiblingClub(null);
          setTenantType(null);
          setError(e.message || 'Club not found');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [host.mode, host.subdomainLabel]);

  const value = useMemo(
    () => ({
      host,
      club,
      pathSlug: club?.slug,
      loading,
      error,
      siblingClub,
      tenantType,
    }),
    [host, club, loading, error, siblingClub, tenantType]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useClubHost(): ClubHostContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      host: getTenantSubdomainContext(),
      club: null,
      pathSlug: undefined,
      loading: false,
      error: null,
      siblingClub: null,
      tenantType: null,
    };
  }
  return ctx;
}
